/**
 * Custom Voice Test Page
 *
 * Allows admins to test any voice with custom text input.
 * Displays grouped voice selector, text input, and audio player.
 *
 * Acceptance Criteria: AC 31, 34
 */

import { useState, useMemo } from 'react';
import { useVoices, useGenerateVoiceSample } from '../services/voiceApi';
import { VoiceNavigation } from '../components/VoiceNavigation';
import { useInitialData } from 'contexts/InitialDataContext';

export function CustomVoiceTestView() {
  const [selectedVoiceId, setSelectedVoiceId] = useState<number | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [text, setText] = useState('');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: voices, isLoading } = useVoices({ isActive: true });
  const { state: initialDataState } = useInitialData();
  const generateSampleMutation = useGenerateVoiceSample();

  const maxTextLength = 500;

  // Create language code to name mapping from initial data
  const languageMap = useMemo(() => {
    if (!initialDataState.data?.languages) return new Map<string, string>();
    return new Map(initialDataState.data.languages.map((lang) => [lang.code, lang.name]));
  }, [initialDataState.data?.languages]);

  // Get voice test templates for selected language from initial data
  const voiceTestTemplates = useMemo(() => {
    if (!selectedLanguage || !initialDataState.data?.voiceTestTemplates) {
      return [];
    }
    return initialDataState.data.voiceTestTemplates[selectedLanguage] || [];
  }, [selectedLanguage, initialDataState.data?.voiceTestTemplates]);

  // Group voices by provider
  const voicesByProvider =
    voices?.reduce(
      (acc, voice) => {
        const providerName = voice.providerName;
        if (!acc[providerName]) {
          acc[providerName] = [];
        }
        acc[providerName].push(voice);
        return acc;
      },
      {} as Record<string, typeof voices>
    ) || {};

  const selectedVoice = voices?.find((v) => v.id === selectedVoiceId);

  const handleGenerateAudio = async () => {
    if (!selectedVoiceId || !text.trim()) {
      setError('Please select a voice and enter text');
      return;
    }

    setError(null);
    setAudioUrl(null);

    try {
      const response = await generateSampleMutation.mutateAsync({
        voiceId: selectedVoiceId,
        text: text.trim(),
      });
      setAudioUrl(response.audioUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate audio');
    }
  };

  const handleTemplateSelect = (templateText: string) => {
    setText(templateText);
  };

  const handleDownload = () => {
    if (audioUrl) {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `voice-sample-${selectedVoiceId}-${Date.now()}.mp3`;
      link.click();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" data-testid="custom-voice-test-view">
      <VoiceNavigation />
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Custom Voice Test</h1>
        <p className="text-gray-600">Test any voice with custom text input</p>
      </div>

      {/* Voice Selector */}
      <div className="mb-6 bg-white rounded-lg shadow-md p-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <label htmlFor="voice-select" className="block text-sm font-medium text-gray-700 mb-2">
              Select Voice <span className="text-red-500">*</span>
            </label>
            <select
              id="voice-select"
              value={selectedVoiceId ?? ''}
              onChange={(e) => {
                const voiceId = e.target.value ? Number(e.target.value) : null;
                setSelectedVoiceId(voiceId);
                // Auto-select first language when voice changes
                const voice = voices?.find((v) => v.id === voiceId);
                setSelectedLanguage(voice?.languageCodes[0] || '');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="voice-select"
            >
              <option value="">Select a voice...</option>
              {Object.entries(voicesByProvider).map(([providerName, providerVoices]) => (
                <optgroup key={providerName} label={providerName}>
                  {providerVoices.map((voice) => (
                    <option key={voice.id} value={voice.id}>
                      {voice.name} - {voice.languageCodes.join(', ')} - {voice.gender}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Language Selector */}
          {selectedVoice && selectedVoice.languageCodes.length > 0 && (
            <div className="w-64">
              <label
                htmlFor="language-select"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Select Language <span className="text-red-500">*</span>
              </label>
              <select
                id="language-select"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="language-select"
              >
                {selectedVoice.languageCodes.map((code) => (
                  <option key={code} value={code}>
                    {languageMap.get(code) || code.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Text Input */}
      <div className="mb-6 bg-white rounded-lg shadow-md p-6">
        <label htmlFor="text-input" className="block text-sm font-medium text-gray-700 mb-2">
          Text to Convert <span className="text-red-500">*</span>
        </label>

        {/* Quick Templates */}
        <div className="mb-3">
          <label htmlFor="template-select" className="block text-xs text-gray-500 mb-1">
            Quick Templates (optional)
          </label>
          <select
            id="template-select"
            onChange={(e) => {
              if (e.target.value) {
                handleTemplateSelect(e.target.value);
                e.target.value = '';
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            data-testid="template-select"
          >
            <option value="">Select a template...</option>
            {voiceTestTemplates.map((template) => (
              <option key={template.label} value={template.text}>
                {template.label}
              </option>
            ))}
          </select>
        </div>

        <textarea
          id="text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={maxTextLength}
          rows={5}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter text to convert to speech..."
          data-testid="text-input"
        />

        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-gray-500">Max {maxTextLength} characters</span>
          <span
            className={`text-xs ${
              text.length >= maxTextLength ? 'text-red-600 font-medium' : 'text-gray-500'
            }`}
            data-testid="character-counter"
          >
            {text.length} / {maxTextLength}
          </span>
        </div>
      </div>

      {/* Generate Button */}
      <div className="mb-6">
        <button
          onClick={handleGenerateAudio}
          disabled={!selectedVoiceId || !text.trim() || generateSampleMutation.isPending}
          className="w-full px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed font-medium"
          data-testid="generate-button"
        >
          {generateSampleMutation.isPending ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin h-5 w-5 mr-3"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating Audio...
            </span>
          ) : (
            'Generate Audio'
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div
          className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md"
          data-testid="error-message"
        >
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Audio Player */}
      {audioUrl && (
        <div className="bg-white rounded-lg shadow-md p-6" data-testid="audio-player-section">
          <h3 className="text-lg font-medium mb-4">Generated Audio</h3>
          <audio controls className="w-full mb-4" data-testid="audio-player">
            <source src={audioUrl} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
          <button
            onClick={handleDownload}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            data-testid="download-button"
          >
            Download Audio
          </button>
        </div>
      )}
    </div>
  );
}
